/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useParams } from "react-router-dom";
import JumboAppTextField from "@jumbo/components/JumboAppTextField";

import { FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";

import Stack from "@mui/material/Stack";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { Card, CardContent, CardHeader } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Cancel } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import { getColorList } from "app/redux/slice/colorSlice";
import { useSnackbar } from "notistack";
import { PRODUCTS } from "app/routes/constant";
import {
  createProduct,
  getByIdProduct,
  updateProduct,
} from "app/redux/slice/productSlice";
import { deleteVariant } from "app/redux/slice/variantSlice";
import { getFabricList } from "app/redux/slice/fabricSlice";
import { getSizeList } from "app/redux/slice/sizeSlice";
import { getCategoryList } from "app/redux/slice/categorySlice";

const AddProduct = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const colorData = useSelector((state) => state?.color);
  const sizeData = useSelector((state) => state?.size);
  const fabricData = useSelector((state) => state?.fabric);
  const categoryData = useSelector((state) => state?.category);

  const { id } = useParams();
  const [product, setProduct] = useState();
  const mobileView = useMediaQuery("(max-width:575px)");

  const validationSchema = Yup.object({
    name: Yup.string().nullable().required("The name is required"),
    productCode: Yup.string().nullable().required("The code is required"),
    categoryId: Yup.object().nullable().required("The category is required"),
    fabric: Yup.array()
      .of(
        Yup.object({
          fabricId: Yup.object().nullable().required("The fabric is required"),
          fabricQuantity: Yup.number()
            .min(0.1, "Fabric quantity must be at least 0.1")
            .required("Fabric quantity is required"),
        })
      )
      .min(1, "At least one fabric is required"),
    baseCostPrice: Yup.number()
      .min(0, "Base cost price must be at least 0")
      .required("Base cost price is required"),
    baseSellingPrice: Yup.number()
      .min(0, "Base selling price must be at least 0")
      .required("Base selling price is required"),
    variants: Yup.array().when("hasVariant", {
      is: true,
      then: Yup.array()
        .of(
          Yup.object({
            name: Yup.string().required("The variant name is required"),
            variantCode: Yup.string().required("The code is required"),
            colorId: Yup.object().nullable().required("The color is required"),
            sizeId: Yup.object().nullable().required("The size is required"),
            fabric: Yup.array()
              .of(
                Yup.object({
                  fabricId: Yup.object()
                    .nullable()
                    .required("The fabric is required"),
                  fabricQuantity: Yup.number()
                    .min(0.1, "Fabric quantity must be at least 0.1")
                    .required("Fabric quantity is required"),
                })
              )
              .min(1, "At least one fabric is required"),
            baseCostPrice: Yup.number()
              .min(0, "Base cost price must be at least 0")
              .required("Base cost price is required"),
            baseSellingPrice: Yup.number()
              .min(0, "Base selling price must be at least 0")
              .required("Base selling price is required"),
          })
        )
        .min(1, "At least one variant is required"),
      otherwise: Yup.array().notRequired(),
    }),
  });

  useEffect(() => {
    dispatch(getColorList());
    dispatch(getSizeList());
    dispatch(getFabricList());
    dispatch(getCategoryList());
  }, []);

  useEffect(() => {
    if (id) {
      dispatch(getByIdProduct(id))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            setProduct(response?.data?.data?.productDetail);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
          }
        });
    }
  }, [dispatch, enqueueSnackbar, id]);

  const handleDeleteVariant = (variantId) => {
    dispatch(deleteVariant(variantId))
      .unwrap()
      .then((response) => {
        if (response.status === 200) {
          enqueueSnackbar(response.data?.responseMessage, {
            variant: "success",
          });
        } else {
          enqueueSnackbar(response.data?.responseMessage, {
            variant: "error",
          });
        }
      });
  };

  return (
    <>
      <Card>
        <CardHeader title={`${!id ? "Add" : "Update"} Product`} />
        <CardContent>
          <Formik
            initialValues={{
              name: product?.name ?? "",
              categoryId: product?.categoryId ?? null,
              fabric: product?.fabric?.length
                ? product?.fabric?.map((f) => ({
                    fabricId: f?.fabricId ?? null,
                    fabricQuantity: f?.fabricQuantity ?? "",
                  }))
                : product?.fabricId
                ? [
                    {
                      fabricId: product?.fabricId ?? null,
                      fabricQuantity: product?.fabricQuantity ?? "",
                    },
                  ]
                : [
                    {
                      fabricId: null,
                      fabricQuantity: "",
                    },
                  ],
              tags: product?.tags ?? [],
              meeshoSku: product?.meeshoSku ?? [],
              amazonSku: product?.amazonSku ?? [],
              flipkartSku: product?.flipkartSku ?? [],
              myntraSku: product?.myntraSku ?? [],
              otherSku: product?.otherSku ?? [],
              productCode: product?.productCode ?? "",
              hasVariant: product?.hasVariant ?? false,
              baseSellingPrice: product?.baseSellingPrice ?? "",
              baseCostPrice: product?.baseCostPrice ?? "",
              description: product?.description ?? "",
              variants: product?.variants?.length
                ? product?.variants?.map((variant) => {
                    return {
                      _id: variant?._id,
                      colorId: variant?.colorId ?? null,
                      sizeId: variant?.sizeId ?? null,
                      variantCode: variant?.variantCode ?? "",
                      name: variant?.name ?? "",
                      meeshoSku: variant?.meeshoSku ?? [],
                      amazonSku: variant?.amazonSku ?? [],
                      flipkartSku: variant?.flipkartSku ?? [],
                      myntraSku: variant?.myntraSku ?? [],
                      otherSku: variant?.otherSku ?? [],
                      baseCostPrice: variant?.baseCostPrice ?? 0,
                      baseSellingPrice: variant?.baseSellingPrice ?? 0,
                      fabric: variant?.fabric?.map((tf) => ({
                        fabricId: tf?.fabricId ?? null,
                        fabricQuantity: tf?.fabricQuantity ?? "",
                      })),
                    };
                  })
                : [
                    {
                      sizeId: null,
                      colorId: null,
                      variantCode: "",
                      name: "",
                      meeshoSku: "",
                      amazonSku: "",
                      flipkartSku: "",
                      myntraSku: "",
                      otherSku: "",
                      baseCostPrice: "",
                      baseSellingPrice: "",
                      fabric: (product?.fabric?.length
                        ? product?.fabric
                        : product?.fabricId
                        ? [
                            {
                              fabricId: product?.fabricId ?? null,
                              fabricQuantity: product?.fabricQuantity ?? "",
                            },
                          ]
                        : [{ fabricId: null, fabricQuantity: "" }]
                      ).map((f) => ({
                        fabricId: f?.fabricId ?? null,
                        fabricQuantity: f?.fabricQuantity ?? "",
                      })),
                    },
                  ],
            }}
            validationSchema={validationSchema}
            enableReinitialize={true}
            validateOnChange={false}
            onSubmit={async (data, { setSubmitting }) => {
              const newData = {
                name: data?.name?.trim(),
                categoryId: data?.categoryId?._id,
                productCode: data?.productCode?.trim(),
                fabric: data?.fabric?.map(({ fabricId, fabricQuantity }) => ({
                  fabricId: fabricId?._id,
                  fabricQuantity,
                })),
                hasVariant: data?.hasVariant,
                baseSellingPrice: data?.baseSellingPrice,
                baseCostPrice: data?.baseCostPrice,
                tags: data?.tags,
                meeshoSku: data?.meeshoSku,
                amazonSku: data?.amazonSku,
                flipkartSku: data?.flipkartSku,
                myntraSku: data?.myntraSku,
                otherSku: data?.otherSku,
                description: data?.description?.trim(),
                ...(data?.hasVariant && {
                  variants: data?.variants?.map((variant) => {
                    return {
                      ...(variant?._id && { _id: variant?._id }),
                      colorId: variant?.colorId?._id,
                      sizeId: variant?.sizeId?._id,
                      variantCode: variant?.variantCode?.trim(),
                      name: variant?.name?.trim(),
                      meeshoSku: variant?.meeshoSku,
                      amazonSku: variant?.amazonSku,
                      flipkartSku: variant?.flipkartSku,
                      myntraSku: variant?.myntraSku,
                      otherSku: variant?.otherSku,
                      fabric: variant?.fabric?.map(
                        ({ fabricId, fabricQuantity }) => ({
                          fabricId: fabricId?._id,
                          fabricQuantity,
                        })
                      ),
                      baseCostPrice: variant?.baseCostPrice,
                      baseSellingPrice: variant?.baseSellingPrice,
                    };
                  }),
                }),
              };

              setSubmitting(true);
              const response = await dispatch(
                id
                  ? updateProduct({ id, data: newData })
                  : createProduct(newData)
              ).unwrap();
              if (response.status === 200) {
                enqueueSnackbar(response.data?.responseMessage, {
                  variant: "success",
                });
                navigate(PRODUCTS);
                setSubmitting(false);
              } else {
                enqueueSnackbar(response.data?.responseMessage, {
                  variant: "error",
                });
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              isSubmitting,
              handleChange,
              handleBlur,
              setFieldValue,
            }) => (
              <Form>
                <div>
                  <div>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={4}>
                        <JumboAppTextField
                          fullWidth
                          name={`name`}
                          label="Product Name*"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <JumboAppTextField
                          fullWidth
                          name={`productCode`}
                          label="Product Code*"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <JumboAutoComplete
                          options={categoryData.list ?? []}
                          label="Category*"
                          size="large"
                          getOptionLabel={(option) => option?.name}
                          value={values?.categoryId}
                          name={"categoryId"}
                          onChange={(evt, value) => {
                            setFieldValue("categoryId", value);
                          }}
                          helperText={errors && errors.categoryId}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FieldArray name={`fabric`}>
                          {({ remove, push }) => (
                            <div>
                              {values?.fabric?.map((fabric, fabricIndex) => (
                                <Grid
                                  container
                                  spacing={2}
                                  alignItems="center"
                                  key={fabricIndex}
                                  mb={1}
                                >
                                  <Grid item xs={12} sm={6}>
                                    <JumboAutoComplete
                                      options={fabricData.list ?? []}
                                      label="Fabric*"
                                      size="large"
                                      getOptionLabel={(option) => option?.name}
                                      value={fabric?.fabricId}
                                      name={`fabric[${fabricIndex}].fabricId`}
                                      onChange={(evt, value) => {
                                        setFieldValue(
                                          `fabric[${fabricIndex}].fabricId`,
                                          value
                                        );
                                        // if (values?.hasVariant) {
                                        const updatedVariants = (
                                          values?.variants ?? []
                                        ).map((v) => {
                                          const fabric = Array.isArray(
                                            v?.fabric
                                          )
                                            ? [...v.fabric]
                                            : [];
                                          const current = fabric[
                                            fabricIndex
                                          ] ?? {
                                            fabricId: null,
                                            fabricQuantity: "",
                                          };
                                          fabric[fabricIndex] = {
                                            ...current,
                                            fabricId: value ?? null,
                                          };
                                          return { ...v, fabric };
                                        });
                                        setFieldValue(
                                          "variants",
                                          updatedVariants,
                                          false
                                        );
                                        // }
                                      }}
                                      helperText={
                                        errors &&
                                        errors.fabric &&
                                        errors.fabric[fabricIndex]
                                          ? errors.fabric[fabricIndex].fabricId
                                          : ""
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={5}>
                                    <TextField
                                      fullWidth
                                      name={`fabric[${fabricIndex}].fabricQuantity`}
                                      label="Required Fabric (in meter)*"
                                      type="number"
                                      value={fabric?.fabricQuantity}
                                      onChange={(e) => {
                                        setFieldValue(
                                          `fabric[${fabricIndex}].fabricQuantity`,
                                          e.target.value
                                        );
                                        // if (values?.hasVariant) {
                                        const newQty = e.target.value;
                                        const updatedVariants = (
                                          values?.variants ?? []
                                        ).map((v) => {
                                          const fabric = Array.isArray(
                                            v?.fabric
                                          )
                                            ? [...v.fabric]
                                            : [];
                                          const current = fabric[
                                            fabricIndex
                                          ] ?? {
                                            fabricId: null,
                                            fabricQuantity: "",
                                          };
                                          fabric[fabricIndex] = {
                                            ...current,
                                            fabricQuantity: newQty,
                                          };
                                          return { ...v, fabric };
                                        });
                                        setFieldValue(
                                          "variants",
                                          updatedVariants,
                                          false
                                        );
                                        // }
                                      }}
                                      error={
                                        errors &&
                                        errors.fabric &&
                                        errors.fabric[fabricIndex]
                                          ? errors.fabric[fabricIndex]
                                              .fabricQuantity
                                          : ""
                                      }
                                      helperText={
                                        errors &&
                                        errors.fabric &&
                                        errors.fabric[fabricIndex]
                                          ? errors.fabric[fabricIndex]
                                              .fabricQuantity
                                          : ""
                                      }
                                    />
                                  </Grid>
                                  {values.fabric.length > 1 && (
                                    <Grid item xs={12} sm={1}>
                                      <IconButton
                                        size="large"
                                        onClick={() => {
                                          remove(fabricIndex);
                                          if (values?.hasVariant) {
                                            const updatedVariants = (
                                              values?.variants ?? []
                                            ).map((v) => ({
                                              ...v,
                                              fabric: (v?.fabric ?? []).filter(
                                                (_, idx) => idx !== fabricIndex
                                              ),
                                            }));
                                            setFieldValue(
                                              "variants",
                                              updatedVariants,
                                              false
                                            );
                                          }
                                        }}
                                      >
                                        <Cancel />
                                      </IconButton>
                                    </Grid>
                                  )}
                                </Grid>
                              ))}
                              <Button
                                variant="outlined"
                                sx={{ mt: 1 }}
                                onClick={() => {
                                  push({ fabricId: null, fabricQuantity: "" });
                                  if (values?.hasVariant) {
                                    const updatedVariants = (
                                      values?.variants ?? []
                                    ).map((v) => ({
                                      ...v,
                                      fabric: [
                                        ...(v?.fabric ?? []),
                                        { fabricId: null, fabricQuantity: "" },
                                      ],
                                    }));
                                    setFieldValue(
                                      "variants",
                                      updatedVariants,
                                      false
                                    );
                                  }
                                }}
                              >
                                Add More Fabric
                              </Button>
                            </div>
                          )}
                        </FieldArray>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          sx={{ width: "100%" }}
                          name="baseCostPrice"
                          type="number"
                          label="Base Cost Price*"
                          value={values?.baseCostPrice}
                          onChange={(e) => {
                            setFieldValue("baseCostPrice", +e.target.value);
                            const variants = values?.variants?.map(
                              (variant) => {
                                return {
                                  ...variant,
                                  baseCostPrice: +e.target.value,
                                };
                              }
                            );
                            setFieldValue("variants", variants);
                          }}
                          error={
                            touched?.baseCostPrice && errors?.baseCostPrice
                          }
                          helperText={
                            touched?.baseCostPrice && errors?.baseCostPrice
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          sx={{ width: "100%" }}
                          name="baseSellingPrice"
                          type="number"
                          label="Base Selling Price*"
                          value={values?.baseSellingPrice}
                          onChange={(e) => {
                            setFieldValue("baseSellingPrice", +e.target.value);
                            const variants = values?.variants?.map(
                              (variant) => {
                                return {
                                  ...variant,
                                  baseSellingPrice: +e.target.value,
                                };
                              }
                            );
                            setFieldValue("variants", variants);
                          }}
                          error={
                            touched?.baseSellingPrice &&
                            errors?.baseSellingPrice
                          }
                          helperText={
                            touched?.baseSellingPrice &&
                            errors?.baseSellingPrice
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <Box>
                          <Autocomplete
                            fullWidth
                            multiple
                            freeSolo
                            options={[]}
                            value={values?.flipkartSku ?? []}
                            onChange={(event, value) => {
                              setFieldValue("flipkartSku", value);
                            }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  key={option + index}
                                  label={option}
                                  {...getTagProps({ index })}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                label="Flipkart Sku"
                                placeholder="Add Flipkart Sku"
                              />
                            )}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <Box>
                          <Autocomplete
                            fullWidth
                            multiple
                            freeSolo
                            options={[]}
                            value={values?.amazonSku ?? []}
                            onChange={(event, value) => {
                              setFieldValue("amazonSku", value);
                            }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  key={option + index}
                                  label={option}
                                  {...getTagProps({ index })}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                label="Amazon Sku"
                                placeholder="Add Amazon Sku"
                              />
                            )}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <Box>
                          <Autocomplete
                            fullWidth
                            multiple
                            freeSolo
                            options={[]}
                            value={values?.meeshoSku ?? []}
                            onChange={(event, value) => {
                              setFieldValue("meeshoSku", value);
                            }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  key={option + index}
                                  label={option}
                                  {...getTagProps({ index })}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                label="Meesho Sku"
                                placeholder="Add Meesho Sku"
                              />
                            )}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <Box>
                          <Autocomplete
                            fullWidth
                            multiple
                            freeSolo
                            options={[]}
                            value={values?.myntraSku ?? []}
                            onChange={(event, value) => {
                              setFieldValue("myntraSku", value);
                            }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  key={option + index}
                                  label={option}
                                  {...getTagProps({ index })}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                label="Myntra Sku"
                                placeholder="Add Myntra Sku"
                              />
                            )}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <Box>
                          <Autocomplete
                            fullWidth
                            multiple
                            freeSolo
                            options={[]}
                            value={values?.otherSku ?? []}
                            onChange={(event, value) => {
                              setFieldValue("otherSku", value);
                            }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  key={option + index}
                                  label={option}
                                  {...getTagProps({ index })}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                label="Other Sku"
                                placeholder="Add Other Sku"
                              />
                            )}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <Box>
                          <Autocomplete
                            fullWidth
                            multiple
                            freeSolo
                            options={[]}
                            value={values?.tags ?? []}
                            onChange={(event, value) => {
                              setFieldValue("tags", value);
                            }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  key={option + index}
                                  label={option}
                                  {...getTagProps({ index })}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                label="Tags"
                                placeholder="Add tag"
                              />
                            )}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <JumboAppTextField
                          fullWidth
                          name={`description`}
                          label="Description"
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} mb={2}>
                        <Grid container alignItems="center" spacing={2}>
                          <Grid item>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  name="variant"
                                  checked={values.hasVariant}
                                  onChange={(e) =>
                                    setFieldValue(
                                      "hasVariant",
                                      e.target.checked
                                    )
                                  }
                                />
                              }
                              label="Has Variant"
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    {values.hasVariant && (
                      <FieldArray name={`variants`}>
                        {({ remove: removeVariant, push: pushVariant }) => (
                          <div>
                            {values?.variants?.length > 0 &&
                              values?.variants?.map(
                                (valVariant, indexVariant) => (
                                  <>
                                    <div key={indexVariant}>
                                      <Grid container spacing={3}>
                                        <Grid item xs={12} sm={3}>
                                          <TextField
                                            sx={{ width: "100%" }}
                                            name={`variants[${indexVariant}].name`}
                                            type="text"
                                            label="Name*"
                                            onChange={(e) => {
                                              setFieldValue(
                                                `variants[${indexVariant}].name`,
                                                e.target.value
                                              );
                                            }}
                                            value={valVariant?.name}
                                            error={
                                              errors &&
                                              errors.variants &&
                                              errors.variants[indexVariant]
                                                ? errors.variants[indexVariant]
                                                    .name
                                                : ""
                                            }
                                            helperText={
                                              errors &&
                                              errors.variants &&
                                              errors.variants[indexVariant]
                                                ? errors.variants[indexVariant]
                                                    .name
                                                : ""
                                            }
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                          <JumboAutoComplete
                                            options={colorData.list ?? []}
                                            label="Color*"
                                            size="large"
                                            getOptionLabel={(option) =>
                                              option?.name
                                            }
                                            value={valVariant?.colorId}
                                            name={`variants[${indexVariant}].colorId`}
                                            onChange={(evt, value) => {
                                              setFieldValue(
                                                `variants[${indexVariant}].colorId`,
                                                value
                                              );
                                            }}
                                            helperText={
                                              errors &&
                                              errors.variants &&
                                              errors.variants[indexVariant]
                                                ? errors.variants[indexVariant]
                                                    .colorId
                                                : ""
                                            }
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                          <JumboAutoComplete
                                            options={sizeData.list ?? []}
                                            label="Size*"
                                            size="large"
                                            getOptionLabel={(option) =>
                                              option?.name
                                            }
                                            value={valVariant?.sizeId}
                                            name={`variants[${indexVariant}].sizeId`}
                                            onChange={(evt, value) => {
                                              setFieldValue(
                                                `variants[${indexVariant}].sizeId`,
                                                value
                                              );
                                            }}
                                            helperText={
                                              errors &&
                                              errors.variants &&
                                              errors.variants[indexVariant]
                                                ? errors.variants[indexVariant]
                                                    .sizeId
                                                : ""
                                            }
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                          <TextField
                                            sx={{ width: "100%" }}
                                            name={`variants[${indexVariant}].variantCode`}
                                            type="text"
                                            label="Variant Code*"
                                            onChange={(e) => {
                                              setFieldValue(
                                                `variants[${indexVariant}].variantCode`,
                                                e.target.value
                                              );
                                            }}
                                            value={valVariant?.variantCode}
                                            error={
                                              errors &&
                                              errors.variants &&
                                              errors.variants[indexVariant]
                                                ? errors.variants[indexVariant]
                                                    .variantCode
                                                : ""
                                            }
                                            helperText={
                                              errors &&
                                              errors.variants &&
                                              errors.variants[indexVariant]
                                                ? errors.variants[indexVariant]
                                                    .variantCode
                                                : ""
                                            }
                                          />
                                        </Grid>

                                        <Grid item xs={12} sm={12}>
                                          <Box>
                                            <Autocomplete
                                              fullWidth
                                              multiple
                                              freeSolo
                                              options={[]}
                                              value={
                                                valVariant?.flipkartSku ?? []
                                              }
                                              onChange={(event, value) => {
                                                setFieldValue(
                                                  `variants[${indexVariant}].flipkartSku`,
                                                  value
                                                );
                                              }}
                                              renderTags={(
                                                value,
                                                getTagProps
                                              ) =>
                                                value.map((option, index) => (
                                                  <Chip
                                                    key={option + index}
                                                    label={option}
                                                    {...getTagProps({ index })}
                                                  />
                                                ))
                                              }
                                              renderInput={(params) => (
                                                <TextField
                                                  {...params}
                                                  variant="outlined"
                                                  label="Flipkart Sku"
                                                  placeholder="Add Flipkart Sku"
                                                />
                                              )}
                                            />
                                          </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={12}>
                                          <Box>
                                            <Autocomplete
                                              fullWidth
                                              multiple
                                              freeSolo
                                              options={[]}
                                              value={
                                                valVariant?.amazonSku ?? []
                                              }
                                              onChange={(event, value) => {
                                                setFieldValue(
                                                  `variants[${indexVariant}].amazonSku`,
                                                  value
                                                );
                                              }}
                                              renderTags={(
                                                value,
                                                getTagProps
                                              ) =>
                                                value.map((option, index) => (
                                                  <Chip
                                                    key={option + index}
                                                    label={option}
                                                    {...getTagProps({ index })}
                                                  />
                                                ))
                                              }
                                              renderInput={(params) => (
                                                <TextField
                                                  {...params}
                                                  variant="outlined"
                                                  label="Amazon Sku"
                                                  placeholder="Add Amazon Sku"
                                                />
                                              )}
                                            />
                                          </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={12}>
                                          <Box>
                                            <Autocomplete
                                              fullWidth
                                              multiple
                                              freeSolo
                                              options={[]}
                                              value={
                                                valVariant?.meeshoSku ?? []
                                              }
                                              onChange={(event, value) => {
                                                setFieldValue(
                                                  `variants[${indexVariant}].meeshoSku`,
                                                  value
                                                );
                                              }}
                                              renderTags={(
                                                value,
                                                getTagProps
                                              ) =>
                                                value.map((option, index) => (
                                                  <Chip
                                                    key={option + index}
                                                    label={option}
                                                    {...getTagProps({ index })}
                                                  />
                                                ))
                                              }
                                              renderInput={(params) => (
                                                <TextField
                                                  {...params}
                                                  variant="outlined"
                                                  label="Meesho Sku"
                                                  placeholder="Add Meesho Sku"
                                                />
                                              )}
                                            />
                                          </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={12}>
                                          <Box>
                                            <Autocomplete
                                              fullWidth
                                              multiple
                                              freeSolo
                                              options={[]}
                                              value={
                                                valVariant?.myntraSku ?? []
                                              }
                                              onChange={(event, value) => {
                                                setFieldValue(
                                                  `variants[${indexVariant}].myntraSku`,
                                                  value
                                                );
                                              }}
                                              renderTags={(
                                                value,
                                                getTagProps
                                              ) =>
                                                value.map((option, index) => (
                                                  <Chip
                                                    key={option + index}
                                                    label={option}
                                                    {...getTagProps({ index })}
                                                  />
                                                ))
                                              }
                                              renderInput={(params) => (
                                                <TextField
                                                  {...params}
                                                  variant="outlined"
                                                  label="Myntra Sku"
                                                  placeholder="Add Myntra Sku"
                                                />
                                              )}
                                            />
                                          </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={12}>
                                          <Box>
                                            <Autocomplete
                                              fullWidth
                                              multiple
                                              freeSolo
                                              options={[]}
                                              value={valVariant?.otherSku ?? []}
                                              onChange={(event, value) => {
                                                setFieldValue(
                                                  `variants[${indexVariant}].otherSku`,
                                                  value
                                                );
                                              }}
                                              renderTags={(
                                                value,
                                                getTagProps
                                              ) =>
                                                value.map((option, index) => (
                                                  <Chip
                                                    key={option + index}
                                                    label={option}
                                                    {...getTagProps({ index })}
                                                  />
                                                ))
                                              }
                                              renderInput={(params) => (
                                                <TextField
                                                  {...params}
                                                  variant="outlined"
                                                  label="Other Sku"
                                                  placeholder="Add Other Sku"
                                                />
                                              )}
                                            />
                                          </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={5.5}>
                                          <FieldArray
                                            name={`variants[${indexVariant}].fabric`}
                                          >
                                            {({ remove, push }) => (
                                              <div>
                                                {valVariant?.fabric?.map(
                                                  (vf, vfIndex) => (
                                                    <Grid
                                                      container
                                                      spacing={2}
                                                      alignItems="center"
                                                      key={vfIndex}
                                                      mb={1}
                                                    >
                                                      <Grid item xs={12} sm={6}>
                                                        <JumboAutoComplete
                                                          options={
                                                            fabricData.list ??
                                                            []
                                                          }
                                                          label="Fabric*"
                                                          size="large"
                                                          getOptionLabel={(
                                                            option
                                                          ) => option?.name}
                                                          value={vf?.fabricId}
                                                          name={`variants[${indexVariant}].fabric[${vfIndex}].fabricId`}
                                                          onChange={() => {}}
                                                          readOnly
                                                          disabled
                                                          helperText={
                                                            errors &&
                                                            errors.variants &&
                                                            errors.variants[
                                                              indexVariant
                                                            ] &&
                                                            errors.variants[
                                                              indexVariant
                                                            ].fabric &&
                                                            errors.variants[
                                                              indexVariant
                                                            ].fabric[vfIndex]
                                                              ? errors.variants[
                                                                  indexVariant
                                                                ].fabric[
                                                                  vfIndex
                                                                ].fabricId
                                                              : ""
                                                          }
                                                        />
                                                      </Grid>
                                                      <Grid item xs={12} sm={6}>
                                                        <TextField
                                                          fullWidth
                                                          name={`variants[${indexVariant}].fabric[${vfIndex}].fabricQuantity`}
                                                          label="Required Fabric (in meter)*"
                                                          type="number"
                                                          value={
                                                            vf?.fabricQuantity
                                                          }
                                                          onChange={(e) => {
                                                            setFieldValue(
                                                              `variants[${indexVariant}].fabric[${vfIndex}].fabricQuantity`,
                                                              e.target.value
                                                            );
                                                          }}
                                                          error={
                                                            errors &&
                                                            errors.variants &&
                                                            errors.variants[
                                                              indexVariant
                                                            ] &&
                                                            errors.variants[
                                                              indexVariant
                                                            ].fabric &&
                                                            errors.variants[
                                                              indexVariant
                                                            ].fabric[vfIndex]
                                                              ? errors.variants[
                                                                  indexVariant
                                                                ].fabric[
                                                                  vfIndex
                                                                ].fabricQuantity
                                                              : ""
                                                          }
                                                          helperText={
                                                            errors &&
                                                            errors.variants &&
                                                            errors.variants[
                                                              indexVariant
                                                            ] &&
                                                            errors.variants[
                                                              indexVariant
                                                            ].fabric &&
                                                            errors.variants[
                                                              indexVariant
                                                            ].fabric[vfIndex]
                                                              ? errors.variants[
                                                                  indexVariant
                                                                ].fabric[
                                                                  vfIndex
                                                                ].fabricQuantity
                                                              : ""
                                                          }
                                                        />
                                                      </Grid>
                                                      {/* {valVariant?.fabric
                                                      ?.length > 1 && (
                                                      <Grid item xs={12} sm={1}>
                                                        <IconButton
                                                          size="large"
                                                          onClick={() =>
                                                            remove(vfIndex)
                                                          }
                                                        >
                                                          <Cancel />
                                                        </IconButton>
                                                      </Grid>
                                                    )} */}
                                                    </Grid>
                                                  )
                                                )}
                                                {/* Variants inherit fabric from main; adding per-variant fabric is disabled */}
                                              </div>
                                            )}
                                          </FieldArray>
                                        </Grid>
                                        <Grid item xs={12} sm={2.5}>
                                          <TextField
                                            sx={{ width: "100%" }}
                                            name={`variants[${indexVariant}].baseCostPrice`}
                                            type="number"
                                            label="Base Cost Price*"
                                            onChange={(e) => {
                                              setFieldValue(
                                                `variants[${indexVariant}].baseCostPrice`,
                                                +e.target.value
                                              );
                                            }}
                                            value={valVariant?.baseCostPrice}
                                            error={
                                              errors &&
                                              errors.variants &&
                                              errors.variants[indexVariant]
                                                ? errors.variants[indexVariant]
                                                    .baseCostPrice
                                                : ""
                                            }
                                            helperText={
                                              errors &&
                                              errors.variants &&
                                              errors.variants[indexVariant]
                                                ? errors.variants[indexVariant]
                                                    .baseCostPrice
                                                : ""
                                            }
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={2.5}>
                                          <TextField
                                            sx={{ width: "100%" }}
                                            name={`variants[${indexVariant}].baseSellingPrice`}
                                            type="number"
                                            label="Base Selling Price*"
                                            onChange={(e) => {
                                              setFieldValue(
                                                `variants[${indexVariant}].baseSellingPrice`,
                                                +e.target.value
                                              );
                                            }}
                                            value={valVariant?.baseSellingPrice}
                                            error={
                                              errors &&
                                              errors.variants &&
                                              errors.variants[indexVariant]
                                                ? errors.variants[indexVariant]
                                                    .baseSellingPrice
                                                : ""
                                            }
                                            helperText={
                                              errors &&
                                              errors.variants &&
                                              errors.variants[indexVariant]
                                                ? errors.variants[indexVariant]
                                                    .baseSellingPrice
                                                : ""
                                            }
                                          />
                                        </Grid>
                                        {values.variants.length !== 1 && (
                                          <Grid item xs={12} sm={1}>
                                            <IconButton
                                              size="large"
                                              onClick={() => {
                                                removeVariant(indexVariant);
                                                if (valVariant?._id) {
                                                  handleDeleteVariant(
                                                    valVariant?._id
                                                  );
                                                }
                                              }}
                                            >
                                              <Cancel />
                                            </IconButton>
                                          </Grid>
                                        )}
                                      </Grid>
                                    </div>
                                    <Divider sx={{ my: 2 }} />
                                  </>
                                )
                              )}
                            <Grid item xs={12} sm={12}>
                              <Button
                                variant="contained"
                                fullWidth
                                sx={{ mt: 1.5 }}
                                onClick={() =>
                                  pushVariant({
                                    name: "",
                                    variantCode: "",
                                    colorId: null,
                                    sizeId: null,
                                    fabric: (values?.fabric ?? []).map((f) => ({
                                      fabricId: f?.fabricId ?? null,
                                      fabricQuantity: f?.fabricQuantity ?? "",
                                    })),
                                    baseCostPrice: values?.baseCostPrice ?? 0,
                                    baseSellingPrice:
                                      values?.baseSellingPrice ?? 0,
                                  })
                                }
                              >
                                Add More Variant
                              </Button>
                            </Grid>
                          </div>
                        )}
                      </FieldArray>
                    )}
                  </div>
                  <br></br>
                </div>
                <Stack
                  spacing={{ xs: 1, sm: 2 }}
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="flex-end"
                  sx={{ width: "100%" }}
                >
                  <LoadingButton
                    variant="outlined"
                    color="secondary"
                    size="large"
                    onClick={() => {
                      navigate(PRODUCTS);
                    }}
                    fullWidth={mobileView}
                  >
                    Cancel
                  </LoadingButton>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    size="large"
                    loading={isSubmitting}
                    loadingIndicator={<CircularProgress size={20} />}
                    fullWidth={mobileView}
                  >
                    Save
                  </LoadingButton>
                </Stack>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </>
  );
};

export default AddProduct;
